import { supabase } from '../../client';
import { CreateOrderParams, Order, PaymentMethod } from '../types/order';
import { DashboardSummary, DashboardSummaryDays, CustomerMonthlyTotals } from '../types/dashboardSummary';
import { DashboardSuggestions, ProductSuggestion } from '../types/orderSearch';
import { format } from 'date-fns';

/**
 * 새로운 주문을 생성합니다.
 */
export async function create({ userId, nickname, orderDate, totalPrice }: CreateOrderParams): Promise<number> {
  const { data, error } = await supabase
    .from('order')
    .insert({
      user_id: userId,
      nickname,
      order_date: orderDate,
      total_price: totalPrice
    })
    .select('id')
    .single();

  if (error) {
    throw error;
  }
  
  return data.id;
}

/**
 * 특정 사용자의 특정 날짜 주문들을 조회합니다.
 */
export async function findByUserAndDate(userId: string, orderDate: string): Promise<Order[]> {
  const { data, error } = await supabase
    .from('order')
    .select('*')
    .eq('user_id', userId)
    .eq('order_date', orderDate)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * 특정 사용자의 특정 날짜에서 킵(is_keep=true) 주문의 닉네임들을 조회합니다.
 * 배송엑셀 킵 배지/킵 제외 다운로드용 — idx_order_user_date 인덱스 사용(최악 886행/일 실측 1.17ms).
 */
export async function findKeepNicknamesByDate(userId: string, orderDate: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('order')
    .select('nickname')
    .eq('user_id', userId)
    .eq('order_date', orderDate)
    .eq('is_keep', true);

  if (error) throw error;
  return (data ?? []).map(row => row.nickname);
}

/**
 * 주문의 총 가격을 수정합니다.
 */
export async function patchTotalPrice(orderId: number, totalPrice: number): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ 
      total_price: totalPrice
    })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문의 닉네임을 수정합니다.
 */
export async function patchNickname(orderId: number, nickname: string): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ 
      nickname
    })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 특정 닉네임의 최근 30일간의 주문(킵 또는 입금 완료)을 조회합니다.
 */
export async function findRecentRelevantOrdersByNickname(
  userId: string,
  nickname: string,
  currentDate: Date,
  excludeOrderId?: number | null
): Promise<Order[]> {
  const thirtyDaysAgo = new Date(currentDate);
  thirtyDaysAgo.setDate(currentDate.getDate() - 30);
  const thirtyDaysInFuture = new Date(currentDate);
  thirtyDaysInFuture.setDate(currentDate.getDate() + 30);

  const thirtyDaysAgoFormatted = format(thirtyDaysAgo, "yyyy-MM-dd");
  const thirtyDaysInFutureFormatted = format(thirtyDaysInFuture, "yyyy-MM-dd");

  let query = supabase
    .from('order')
    .select('*')
    .eq('user_id', userId)
    .eq('nickname', nickname)
    .gte('order_date', thirtyDaysAgoFormatted)      // 30일 전부터
    .lte('order_date', thirtyDaysInFutureFormatted) // 30일 후까지
    .or('is_keep.eq.true,status.eq.입금 완료');

  if (excludeOrderId) {
    query = query.neq('id', excludeOrderId); // 자기 자신은 제외
  }

  const { data, error } = await query.order('order_date', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * 주문 단건의 status를 조회합니다. (부분입금 자동 전환 reconcile용, 스펙 §16.10)
 * RLS(auth.uid() = user_id)가 소유권을 강제 — 미소유/부재는 null.
 */
export async function findStatusById(orderId: number): Promise<string | null> {
  const { data, error } = await supabase
    .from('order')
    .select('status')
    .eq('id', orderId)
    .maybeSingle();

  if (error) throw error;
  return data?.status ?? null;
}

/**
 * 주문의 is_keep 상태를 수정합니다.
 */
export async function patchIsKeep(orderId: number, isKeep: boolean): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ is_keep: isKeep })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문의 status를 수정합니다.
 */
export async function patchStatus(orderId: number, status: string): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ status: status })
    .eq('id', orderId);

  if (error) throw error;
}


/**
 * 닉네임 부분일치로 주문 id를 검색합니다. order_date desc, id desc 정렬은 서버(RPC)가 보장.
 * 윈도우는 서버 KST -90~+30일 (search_order_ids_by_item_name_rpc와 대칭).
 * isPartial 판정 500은 RPC의 LIMIT 500과 동기화된 상수 — RPC LIMIT이 바뀌면 이 값도 함께 바꿀 것.
 */
export async function searchOrderIdsByNickname(q: string): Promise<{ orderIds: number[]; isPartial: boolean }> {
  const RPC_LIMIT = 500; // search_order_ids_by_nickname_rpc의 LIMIT 500과 동기화
  const { data, error } = await supabase.rpc('search_order_ids_by_nickname_rpc', { p_q: q });
  if (error) throw error;
  const rows = (data ?? []) as { order_id: number }[];
  return {
    orderIds: rows.map(row => row.order_id),
    isPartial: rows.length >= RPC_LIMIT,
  };
}

/**
 * 주문의 메모를 수정합니다.
 */
export async function patchMemo(orderId: number, memo: string): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ memo })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문의 결제수단을 수정합니다. (null = 미기재로 되돌림)
 */
export async function patchPaymentMethod(orderId: number, paymentMethod: PaymentMethod | null): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ payment_method: paymentMethod })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문의 shipping_fee_override를 수정합니다.
 */
export async function patchShippingFeeOverride(orderId: number, value: number | null): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ shipping_fee_override: value })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문을 삭제합니다. (CASCADE로 연관된 items도 함께 삭제)
 */
export async function deleteById(orderId: number): Promise<void> {
  const { error } = await supabase
    .from('order')
    .delete()
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 주문의 날짜를 수정합니다.
 */
export async function patchOrderDate(orderId: number, orderDate: string): Promise<void> {
  const { error } = await supabase
    .from('order')
    .update({ order_date: orderDate })
    .eq('id', orderId);

  if (error) throw error;
}

/**
 * 여러 주문의 날짜를 일괄 수정합니다.
 */
export async function patchOrderDateBulk(orderIds: number[], orderDate: string): Promise<void> {
  if (!orderIds || orderIds.length === 0) return;
  const { error } = await supabase
    .from('order')
    .update({ order_date: orderDate })
    .in('id', orderIds);

  if (error) throw error;
}
/**
 * 특정 시각 이후 생성된 주문 존재 여부만 확인합니다 (경량 판별용 — 출시 팝업 타겟팅 등).
 * order_date는 수정 가능한 영업일자라 활동 판별엔 created_at을 쓴다.
 */
export async function existsOrderSince(userId: string, sinceIso: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('order')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', sinceIso)
    .limit(1);

  if (error) throw error;
  return (data?.length ?? 0) > 0;
}

/**
 * 대시보드 요약(서버 집계 RPC)을 조회합니다. 윈도우는 서버 KST 기준 -days~+30일 (서버가 30~90 클램프).
 */
export async function fetchDashboardSummary(days: DashboardSummaryDays): Promise<DashboardSummary> {
  const { data, error } = await supabase.rpc('get_dashboard_summary_rpc', { p_days: days });
  if (error) throw error;
  return data as DashboardSummary;
}

/**
 * 고객(닉네임)별 월 구매 총액을 조회합니다(서버 집계 RPC).
 * month는 'YYYY-MM-01' 형식. 서버가 [KST 당월-2, KST 당월]로 클램프하고
 * 실제 조회한 월을 응답 month로 에코하므로, 라벨은 요청값이 아니라 응답값을 써야 한다.
 */
export async function fetchCustomerMonthlyTotals(month: string): Promise<CustomerMonthlyTotals> {
  const { data, error } = await supabase.rpc('get_customer_monthly_totals_rpc', { p_month: month });
  if (error) throw error;
  // 함수가 STRICT라 p_month가 NULL로 전달되면 본문 실행 없이 NULL이 온다. 유효한 월에서는
  // 항상 1행이 나오므로 null은 곧 "인자 누락" = 호출자 버그다. 삼키지 말고 올린다.
  if (data === null) throw new Error('get_customer_monthly_totals_rpc: p_month가 전달되지 않았습니다');
  return data as CustomerMonthlyTotals;
}

/**
 * 한 달(p_month가 속한 달의 1일~말일) 동안 많이 주문된 상품을 수량순으로 조회합니다(서버 집계 RPC).
 * month는 'YYYY-MM-01' 형식. 동률은 서버가 상품명 순으로 고정한다.
 */
export async function fetchTopProductsByMonth(
  month: string,
  limit: number,
): Promise<{ product_item_name: string; total_quantity_sold: number }[]> {
  const { data, error } = await supabase.rpc('get_top_products_by_month_rpc', { p_limit: limit, p_month: month });
  if (error) throw error;
  return data || [];
}

/**
 * 대시보드 통합 검색 자동완성 제안(고객 닉네임 + 상품명)을 조회합니다.
 * customers: 닉네임 오름차순 최대 10, products: 최근 주문일 내림차순 최대 8 (서버 DEFAULT 사용, 파라미터 미전달).
 * 서버가 LIKE 메타문자 escape + btrim 처리 — 클라이언트 가공 불필요.
 */
export async function fetchDashboardSuggestions(q: string): Promise<DashboardSuggestions> {
  const { data, error } = await supabase.rpc('get_dashboard_search_suggestions_rpc', { p_q: q });
  if (error) throw error;
  const result = data as { customers?: unknown; products?: unknown } | null;
  return {
    customers: Array.isArray(result?.customers) ? result.customers as string[] : [],
    products: Array.isArray(result?.products) ? result.products as ProductSuggestion[] : [],
  };
}

/**
 * 상품명으로 주문 id를 검색합니다. order_date desc, id desc 정렬은 서버(RPC)가 보장.
 * isPartial 판정 500은 RPC의 LIMIT 500과 동기화된 상수 — RPC LIMIT이 바뀌면 이 값도 함께 바꿀 것.
 */
export async function searchOrderIdsByItemName(q: string): Promise<{ rows: { orderId: number; matchedItemIds: number[] }[]; isPartial: boolean }> {
  const RPC_LIMIT = 500; // search_order_ids_by_item_name_rpc의 LIMIT 500과 동기화
  const { data, error } = await supabase.rpc('search_order_ids_by_item_name_rpc', { p_q: q });
  if (error) throw error;
  const rows = (data ?? []) as { order_id: number; matched_item_ids: number[] }[];
  return {
    rows: rows.map(row => ({ orderId: row.order_id, matchedItemIds: row.matched_item_ids ?? [] })),
    isPartial: rows.length >= RPC_LIMIT,
  };
}

/**
 * 여러 주문 id로 주문을 조회하고, count: 'exact'로 청크별 잘림(max_rows)을 감지합니다.
 * orderItemRepository.findByOrderIdsWithPartialCheck의 500 청크 패턴을 복제.
 * 정렬은 여기서 하지 않음(호출자 책임 — 청크 분할로 전역 정렬 불가).
 * ★user_id 명시 필터 = RLS와 무관하게 항상 본인 주문만(방어). order 테이블에 dev 디버그 RLS
 * 정책이 존재해 RLS만으로는 dev 계정 교차조회가 가능 — 입금 후보 쿼리(depositRepository.findOrdersByIds) 선례와 동일한 방어.
 */
export async function findOrdersByIds(userId: string, orderIds: number[]): Promise<{ orders: Order[]; isPartial: boolean }> {
  if (orderIds.length === 0) return { orders: [], isPartial: false };
  const CHUNK_SIZE = 500;
  const chunks: number[][] = [];
  for (let i = 0; i < orderIds.length; i += CHUNK_SIZE) {
    chunks.push(orderIds.slice(i, i + CHUNK_SIZE));
  }

  const results: Order[] = [];
  let isPartial = false;
  for (const chunk of chunks) {
    const { data, count, error } = await supabase
      .from('order')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .in('id', chunk);

    if (error) throw error;
    if (data) {
      results.push(...data);
      if (count !== null && data.length !== count) isPartial = true;
    }
  }

  return { orders: results, isPartial };
}
